import { InfiniteData, QueryClient } from '@tanstack/react-query';
import { DeleteVideoRequest, Video, VideoResponse } from '../API/types/Videos';

const ACTIVE_CUSTOM_VOD_STATUSES = new Set([
  'pending',
  'startedProcessing',
  'failedToRequest',
  'failedToUpload',
  'reinit',
]);

type VideoDeleteContext = Pick<Video, 'isVod' | 'alertId' | 'status' | 'requestedByUserId'>;

export function normalizeUserId(userId: unknown): string | undefined {
  if (!userId) {
    return undefined;
  }

  if (typeof userId === 'string') {
    return userId;
  }

  if (typeof userId === 'object' && userId !== null && '_id' in userId) {
    return normalizeUserId((userId as { _id?: unknown })._id);
  }

  return String(userId);
}

export function isCustomVodVideo(video: Pick<Video, 'isVod'>): boolean {
  return video.isVod === true;
}

export function isActiveCustomVodVideo(video: Pick<Video, 'isVod' | 'status'>): boolean {
  return isCustomVodVideo(video) && ACTIVE_CUSTOM_VOD_STATUSES.has(video.status);
}

export function isOwnCustomVodRequest(
  video: VideoDeleteContext,
  currentUserId?: string,
): boolean {
  if (!isCustomVodVideo(video)) {
    return false;
  }

  const requesterId = normalizeUserId(video.requestedByUserId);
  const normalizedCurrentUserId = normalizeUserId(currentUserId);

  if (!requesterId || !normalizedCurrentUserId) {
    return false;
  }

  return requesterId === normalizedCurrentUserId;
}

export function isOwnPendingCustomVodRequest(
  video: VideoDeleteContext,
  currentUserId?: string,
): boolean {
  return isOwnCustomVodRequest(video, currentUserId) && video.status === 'pending';
}

export function canShowVideoDeleteAction(
  video: VideoDeleteContext & { alertId?: string | null },
  currentUserId?: string,
  isFleetViewer = false,
): boolean {
  if (isFleetViewer) {
    return false;
  }

  if (isCustomVodVideo(video)) {
    return isOwnCustomVodRequest(video, currentUserId);
  }

  return true;
}

export function resolveVodRequesterUserId(
  video: Pick<Video, 'isVod' | 'status'>,
  currentUserId?: string,
): string | undefined {
  if (!isActiveCustomVodVideo(video)) {
    return undefined;
  }
  return normalizeUserId(currentUserId);
}

export function buildDeleteVideoRequest(
  fleetId: string,
  video: Pick<Video, '_id' | 'isVod' | 'alertId' | 'status' | 'requestedByUserId'>,
  currentUserId?: string,
): DeleteVideoRequest {
  const vodRequesterUserId = resolveVodRequesterUserId(video, currentUserId);

  return {
    fleetId,
    id: video._id,
    ...(vodRequesterUserId ? { vodRequesterUserId } : {}),
  };
}

export function findVideosByIds(
  queryClient: QueryClient,
  fleetId: string,
  ids: string[],
): Video[] {
  const videoById = new Map<string, Video>();

  queryClient.getQueriesData<VideoResponse>({ queryKey: ['videos', fleetId] })
    .forEach(([, data]) => {
      data?.results?.forEach((video) => {
        videoById.set(video._id, video);
      });
    });

  queryClient.getQueriesData<InfiniteData<VideoResponse>>({ queryKey: ['videos-lazy', fleetId] })
    .forEach(([, data]) => {
      data?.pages?.forEach((page) => {
        page.results?.forEach((video) => {
          videoById.set(video._id, video);
        });
      });
    });

  ids.forEach((id) => {
    if (videoById.has(id)) {
      return;
    }

    const cachedVideo = queryClient.getQueryData<Video>(['video', id, fleetId]);
    if (cachedVideo) {
      videoById.set(id, cachedVideo);
    }
  });

  return ids
    .map((id) => videoById.get(id))
    .filter((video): video is Video => !!video);
}

export function buildDeleteVideosRequest(
  queryClient: QueryClient,
  fleetId: string,
  ids: string[],
  currentUserId?: string,
): DeleteVideoRequest[] {
  const videos = findVideosByIds(queryClient, fleetId, ids);

  return ids.map((id) => {
    const video = videos.find((item) => item._id === id);

    if (video) {
      return buildDeleteVideoRequest(fleetId, video, currentUserId);
    }

    return { fleetId, id };
  });
}
